import { z } from 'zod';
import { type ChecklistDef, CHECKLIST_DEFAULTS, MIN_PHOTOS } from './field';
import type { TaskType } from './planning';
import { PROTOCOL_KINDS, type ProtocolKind, type ProtocolTemplateDef, TASK_PROTOCOL_KIND } from './protocol';

/**
 * Ustawienia wykonania. Branża definiuje realizację: czynności (w kolejności wykonywania, ze zdjęciami
 * i wartościami) + minimum zdjęć + protokół odbioru (wzór branży albo ogólny). Planując realizację wybiera się
 * branże sprawy — wspólne zadanie dostaje czynności wszystkich branż (pogrupowane) i wspólny protokół.
 * Wizja, pomiary, serwis, przegląd i inne zadania mają listy ogólne. Zmiany dotyczą nowych zadań i protokołów.
 */

const str = (max: number) => z.string().trim().max(max);

export const checklistItemSchema = z.object({
  label: str(200).min(2, 'Podaj nazwę czynności'),
  required: z.boolean(),
  requiresPhoto: z.boolean(),
  valueType: z.enum(['liczba', 'tekst']).nullable(),
  unit: str(10).default(''),
});

export const workSchema = z.object({
  items: z.array(checklistItemSchema).min(1, 'Lista musi mieć co najmniej jedną czynność').max(40, 'Najwyżej 40 czynności'),
  minPhotos: z.number().int().min(0).max(20),
});
export type WorkInput = z.infer<typeof workSchema>;

/** Rodzaje zadań z listą czynności — opis w ustawieniach. */
export const WORK_STAGES: Record<TaskType, { label: string; hint: string }> = {
  wizja: { label: 'Wizja lokalna', hint: 'przed ofertą, lista wspólna dla wszystkich branż' },
  pomiary: { label: 'Pomiary / dobór', hint: 'przed ofertą, lista wspólna dla wszystkich branż' },
  montaz: { label: 'Realizacja', hint: 'po umowie — wymaga warunków startu, kończy się protokołem odbioru' },
  uruchomienie: { label: 'Uruchomienie (dawne zadania)', hint: 'teraz część realizacji' },
  serwis: { label: 'Serwis, naprawa', hint: 'zgłoszenia serwisowe, z protokołem serwisowym' },
  przeglad: { label: 'Przegląd okresowy', hint: 'przeglądy z karty instalacji, z protokołem przeglądu' },
  inne: { label: 'Inne zadanie', hint: 'bez protokołu' },
  poprawka: { label: 'Poprawka', hint: 'po odbiorze z uwagami klienta — bez warunków startu, bez protokołu' },
  wlasne: { label: 'Moje zadanie', hint: 'wpis w „Moim kalendarzu” — bez czynności i protokołu' },
};
/** Listy ogólne (w ustawieniach „Wszystkie branże”). */
export const GENERAL_WORK_TYPES: TaskType[] = ['wizja', 'pomiary', 'montaz', 'serwis', 'przeglad', 'poprawka', 'inne'];
/** Rodzaje zadań z możliwą własną listą branży (realizacja, przegląd, serwis — QA5 P30); pozostałe listy są ogólne. */
export const TRADE_WORK_TYPES: TaskType[] = ['montaz', 'przeglad', 'serwis'];
export const workProtocolKind = (t: TaskType): ProtocolKind | null => TASK_PROTOCOL_KIND[t] ?? null;

/** Lista czynności: realizacji branży (tradeCode = kod branży, rodzaj „montaz”) albo ogólna rodzaju zadania („*”). */
export interface WorkDto {
  taskType: TaskType;
  tradeCode: string;
  items: ChecklistDef[];
  minPhotos: number;
  /** false — wbudowana lista domyślna (jeszcze nie zmieniana) */
  custom: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** Wbudowana lista ogólna rodzaju zadania. */
export const defaultWork = (taskType: TaskType): { items: ChecklistDef[]; minPhotos: number } => ({
  items: CHECKLIST_DEFAULTS[taskType] ?? CHECKLIST_DEFAULTS.inne,
  minPhotos: MIN_PHOTOS[taskType] ?? 0,
});

const fieldKey = z
  .string()
  .trim()
  .regex(/^[a-z0-9_]{1,40}$/, 'Klucz pola: małe litery, cyfry, podkreślenie');

export const protocolFieldSchema = z
  .object({
    /** pusty — nadawany z nazwy przy zapisie */
    key: z.union([fieldKey, z.literal('')]).default(''),
    label: str(200).min(2, 'Podaj nazwę pola'),
    type: z.enum(['liczba', 'tekst', 'tak_nie', 'wybor']),
    unit: str(10).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    required: z.boolean(),
    options: z.array(str(80).min(1)).max(20).optional(),
  })
  .refine((f) => f.type !== 'wybor' || (f.options?.length ?? 0) >= 2, { message: 'Pole wyboru potrzebuje co najmniej dwóch opcji', path: ['options'] });

export const protocolTemplateEditSchema = z.object({
  title: str(200).min(3, 'Podaj tytuł protokołu'),
  sections: z
    .array(z.object({ title: str(120).min(2, 'Podaj nazwę sekcji'), fields: z.array(protocolFieldSchema).max(40) }))
    .min(1, 'Protokół musi mieć co najmniej jedną sekcję')
    .max(15),
  devices: z.boolean(),
  declarations: z.array(str(600).min(3)).max(10),
});
export type ProtocolTemplateEdit = z.infer<typeof protocolTemplateEditSchema>;

/** Wzór protokołu (najnowsza wersja) do edycji. */
export interface ProtocolTemplateDto {
  code: string;
  kind: ProtocolKind;
  kindLabel: string;
  tradeCode: string;
  version: number;
  definition: ProtocolTemplateDef;
  createdAt: string;
  /** liczba podpisanych protokołów z wcześniejszych wersji tego wzoru (zostają bez zmian) */
  signedCount: number;
}

/** Klucz pola z nazwy: „Ciśnienie w instalacji” → „cisnienie_w_instalacji”. */
export function fieldKeyFromLabel(label: string, taken: Set<string>): string {
  const base =
    label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ł/g, 'l')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 34) || 'pole';
  let key = base;
  for (let i = 2; taken.has(key); i++) key = `${base}_${i}`;
  taken.add(key);
  return key;
}

export const protocolKindLabel = (k: ProtocolKind) => PROTOCOL_KINDS[k];
